import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useInkDeviceProfile from "../hooks/useInkDeviceProfile";
import { getInkWritingToolLabel } from "../lib/inkDeviceProfile";

function formatCalibrationDate(value) {
    if (!value) return "Not calibrated";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Saved on this device";
    return date.toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit"
    });
}

function getInkFeelLabel(settings) {
    const smoothing = Number(settings?.smoothingStrength || 0);
    if (smoothing >= 0.28) return "Smooth";
    if (smoothing > 0.1) return "Steady";
    return "Direct";
}

function Profile({ user, apiBase, token, isOnboarding, onProfileSaved }) {
    const navigate = useNavigate();
    const {
        profile: inkDeviceProfile,
        runtimeSettings: inkSettings,
        calibrated: inkCalibrated
    } = useInkDeviceProfile({ apiBase, token });
    const [fullName, setFullName] = useState("");
    const [username, setUsername] = useState("");
    const [phone, setPhone] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [grade, setGrade] = useState("");
    const [schoolName, setSchoolName] = useState("");
    const [city, setCity] = useState("");
    const [stateName, setStateName] = useState("");
    const [memberSince, setMemberSince] = useState("");
    const [photoUrl, setPhotoUrl] = useState("");
    const [photoDataUrl, setPhotoDataUrl] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveStatus, setSaveStatus] = useState("idle");
    const [usernameError, setUsernameError] = useState("");
    const [isUsernameLocked, setIsUsernameLocked] = useState(false);
    const [activePanel, setActivePanel] = useState(isOnboarding ? "profile" : null);

    // Role and Tutor Profile State
    const [role, setRole] = useState("student");
    const [learningGoal, setLearningGoal] = useState("boards_and_concepts");
    const [organization, setOrganization] = useState("");
    const [subjects, setSubjects] = useState("Mathematics");
    const [bio, setBio] = useState("");
    const [inviteCode, setInviteCode] = useState("");
    const [copiedInviteCode, setCopiedInviteCode] = useState(false);

    // Student's connected tutors
    const [connectedTutors, setConnectedTutors] = useState([]);
    const [tutorJoinCode, setTutorJoinCode] = useState("");
    const [tutorJoinStatus, setTutorJoinStatus] = useState("");
    const [tutorJoinError, setTutorJoinError] = useState("");
    const [isEnrolledStudent, setIsEnrolledStudent] = useState(false);
    const [autoFilledCode, setAutoFilledCode] = useState("");

    // Read tutorCode from query parameters or localStorage on mount
    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search);
            const urlCode = params.get("tutorCode") || params.get("code") || "";
            const savedCode = localStorage.getItem("inksolver_pending_tutor_code") || "";
            const codeToUse = urlCode.trim() || savedCode.trim();
            if (codeToUse) {
                setTutorJoinCode(codeToUse);
                setAutoFilledCode(codeToUse);
                localStorage.setItem("inksolver_pending_tutor_code", codeToUse);
            }
        } catch (_) {}
    }, []);

    useEffect(() => {
        if (isOnboarding) setActivePanel("profile");
    }, [isOnboarding]);

    async function handleSave() {
        if (!username.trim()) {
            setUsernameError("Username is required.");
            return;
        }
        if (!phone) {
            setPhoneError("Phone number is required");
            return;
        }
        if (phone.length !== 10) {
            setPhoneError("Phone number must be 10 digits");
            return;
        }
        if (role === "student" && !grade) {
            setSaveError("Please select a grade");
            setSaveStatus("error");
            return;
        }
        setSaveStatus("saving");
        setSaveError("");

        try {
            let finalPhotoUrl = photoUrl;

            if (photoDataUrl && photoFile) {
                setPhotoUploading(true);
                const avatarRes = await fetch(`${apiBase}/api/me/avatar`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ dataUrl: photoDataUrl, contentType: photoFile.type }),
                });

                if (!avatarRes.ok) {
                    throw new Error("Failed to upload avatar");
                }
                const avatarData = await avatarRes.json();
                finalPhotoUrl = avatarData.picture;
                setPhotoUrl(finalPhotoUrl);
                setPhotoFile(null);
            }

            const response = await fetch(`${apiBase}/api/me/profile`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    fullName,
                    username,
                    phone,
                    grade: role === "student" ? grade : (grade || "N/A"),
                    schoolName,
                    city,
                    state: stateName,
                    role,
                    learningGoal: role === "student" ? learningGoal : undefined,
                    tutorProfile: role === "tutor" ? {
                        organization,
                        subjects: subjects.split(",").map((s) => s.trim()).filter(Boolean),
                        bio
                    } : undefined
                })
            });

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body.error || "Failed to save profile");
            }

            const savedUser = await response.json();
            if (savedUser.tutorProfile?.inviteCode) {
                setInviteCode(savedUser.tutorProfile.inviteCode);
            }

            setSaveStatus("saved");
            setTimeout(() => setSaveStatus("idle"), 3000);
            // Notify App.js so sidebar updates immediately (username, photo, fullName, role)
            if (onProfileSaved) {
                onProfileSaved(username, photoDataUrl || finalPhotoUrl, fullName, true, role);
            }
        }
        catch (err) {
            setSaveError(err.message || "Could not save. Please try again.");
            setSaveStatus("error");
        } finally {
            setPhotoUploading(false);
        }
    }

    async function handleJoinTutor() {
        if (!tutorJoinCode.trim()) return;
        setTutorJoinError("");
        setTutorJoinStatus("Connecting...");
        try {
            const res = await fetch(`${apiBase}/api/student/tutors/join`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ codeOrUsername: tutorJoinCode.trim() })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to join tutor");
            setTutorJoinStatus(data.message || "Connected!");
            setTutorJoinCode("");
            const resTutors = await fetch(`${apiBase}/api/student/my-tutors`, { headers: { Authorization: `Bearer ${token}` } });
            if (resTutors.ok) {
                const tData = await resTutors.json();
                const tutors = tData.tutors || [];
                setConnectedTutors(tutors);
                setIsEnrolledStudent(tutors.length > 0);
            }
            setTimeout(() => setTutorJoinStatus(""), 3000);
        } catch (err) {
            setTutorJoinError(err.message || "Could not connect to tutor");
            setTutorJoinStatus("");
        }
    }

    async function handleDisconnectTutor(tutorUid) {
        if (!window.confirm("Are you sure you want to disconnect from this tutor?")) return;
        try {
            const res = await fetch(`${apiBase}/api/student/tutors/${tutorUid}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                setConnectedTutors((prev) => {
                    const next = prev.filter((t) => t.uid !== tutorUid);
                    setIsEnrolledStudent(next.length > 0);
                    return next;
                });
            }
        } catch (_) {}
    }

    function handleCancel() {
        if (isOnboarding) {
            navigate(-1);
            return;
        }
        setActivePanel(null);
    }

    useEffect(() => {
        if (!token) return;

        async function loadProfile() {
            try {
                const response = await fetch(`${apiBase}/api/me`, {
                    headers: {
                        "Authorization": `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    const body = await response.json().catch(() => ({}));
                    throw new Error(body.error || "Failed to load profile");
                }

                const data = await response.json();
                setFullName(data.name || "");
                setUsername(data.username || "");
                setPhone(data.phone || "");
                setGrade(data.grade || "");
                setSchoolName(data.schoolName || "");
                setCity(data.city || "");
                setStateName(data.state || "");
                setPhotoUrl(data.picture || "");
                setRole(data.role || "student");
                setLearningGoal(data.learningGoal || "boards_and_concepts");

                if (data.tutorProfile) {
                    setOrganization(data.tutorProfile.organization || "");
                    setSubjects(Array.isArray(data.tutorProfile.subjects) ? data.tutorProfile.subjects.join(", ") : "Mathematics");
                    setBio(data.tutorProfile.bio || "");
                    setInviteCode(data.tutorProfile.inviteCode || "");
                }

                if ((data.role || "student") === "student") {
                    fetch(`${apiBase}/api/student/my-tutors`, { headers: { Authorization: `Bearer ${token}` } })
                        .then((res) => (res.ok ? res.json() : { tutors: [] }))
                        .then((tData) => {
                            const tutors = tData.tutors || [];
                            setConnectedTutors(tutors);
                            setIsEnrolledStudent(tutors.length > 0);
                        })
                        .catch(() => {});
                }

                // Lock username if already set in DB
                if (data.username) {
                    setIsUsernameLocked(true);
                }
                if (data.createdAt) {
                    const date = new Date(data.createdAt);
                    const formatted = date.toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                    });
                    setMemberSince(formatted);
                }
            } catch (err) {
                setLoadError(err.message || "Could not load your profile");
            }
        }
        loadProfile();
    }, [apiBase, token]);

    function handlePhotoChange(e) {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            alert("Please select an image file.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            alert("Image too large. Maximum size is 5MB.");
            return;
        }

        setPhotoFile(file);

        const reader = new FileReader();
        reader.onload = (event) => {
            setPhotoDataUrl(event.target.result);
        };
        reader.readAsDataURL(file);
    }

    const initials = fullName
        ? fullName.trim().split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase()
        : username
            ? username.slice(0, 2).toUpperCase()
            : user?.email
                ? user.email.slice(0, 2).toUpperCase()
                : "??";

    return (
        <section className="page">
            {/* Hide Back button during onboarding — user must complete profile first */}
            {!isOnboarding && (
                <div>
                    <button className="secondary-button" onClick={() => navigate(-1)}>
                        ← Back
                    </button>
                </div>
            )}

            {isOnboarding && (
                <div className="profile-onboarding-banner">
                    👋 Welcome! Please complete your profile to get started.
                </div>
            )}

            {loadError && (
                <div className="error-text">{loadError}</div>
            )}

            <div className="profile-page-wrap">
                <div>
                    <p className="page-sub" style={{ textTransform: "uppercase", fontSize: "11px", marginBottom: "4px" }}>
                        Profile Page
                    </p>
                    <h2 className="page-title">Your profile</h2>
                    <p className="page-sub">Open only the section you want to review or update.</p>
                </div>
                <div className="profile-panel-launchers" aria-label="Profile sections">
                    <button
                        type="button"
                        className={`profile-panel-launcher ${activePanel === "profile" ? "is-active" : ""}`.trim()}
                        aria-expanded={activePanel === "profile"}
                        aria-controls="profile-personal-panel"
                        onClick={() => setActivePanel((current) => current === "profile" && !isOnboarding ? null : "profile")}
                    >
                        <span className="profile-panel-number">01</span>
                        <span className="profile-panel-copy">
                            <strong>Personal profile</strong>
                            <span>{fullName || username || "Your account details"}</span>
                            <small>{grade || "Name, school, grade and contact information"}</small>
                        </span>
                        <span className="profile-panel-action">{activePanel === "profile" ? "Minimize" : "Open"}</span>
                    </button>
                    <button
                        type="button"
                        className={`profile-panel-launcher ${activePanel === "calibration" ? "is-active" : ""}`.trim()}
                        aria-expanded={activePanel === "calibration"}
                        aria-controls="profile-calibration-panel"
                        disabled={isOnboarding}
                        onClick={() => setActivePanel((current) => current === "calibration" ? null : "calibration")}
                    >
                        <span className="profile-panel-number">02</span>
                        <span className="profile-panel-copy">
                            <strong>Writing calibration</strong>
                            <span>{inkCalibrated ? getInkWritingToolLabel(inkSettings.writingTool) : "Optional setup"}</span>
                            <small>{isOnboarding ? "Available after saving your profile" : inkCalibrated ? `Saved ${formatCalibrationDate(inkDeviceProfile?.calibratedAt)}` : "Tune this browser and stylus when you are ready"}</small>
                        </span>
                        <span className="profile-panel-action">{isOnboarding ? "Next step" : activePanel === "calibration" ? "Minimize" : "Open"}</span>
                    </button>
                </div>
                {activePanel === "profile" && (
                <div id="profile-personal-panel" className="profile-expanded-panel">
                <div className="profile-layout">
                    <div className="profile-left-card">
                        <div className="profile-avatar">
                            {(photoDataUrl || photoUrl)
                                ? <img src={photoDataUrl || photoUrl} alt="Profile" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                                : initials
                            }
                        </div>
                        <div className="profile-name">
                            {fullName || "Your Name"}
                        </div>
                        <div className="profile-username">
                            {username || "username"}
                        </div>

                        {/* Hidden file input — button triggers it */}
                        <input
                            type="file"
                            id="avatar-file-input"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handlePhotoChange}
                        />
                        <button
                            className="secondary-button"
                            onClick={() => document.getElementById("avatar-file-input").click()}
                            disabled={photoUploading}
                        >
                            {photoUploading ? "Uploading..." : "Change photo"}
                        </button>
                        <div className="profile-meta-row">
                            <span>Member since</span>
                            <span>{memberSince || "—"}</span>
                        </div>

                        <div className="profile-meta-row">
                            <span>Plan</span>
                            <span className="profile-meta-value-green">Free</span>
                        </div>
                    </div>

                    <div className="profile-right-card">
                        <div className="form-group" style={{ marginBottom: "22px" }}>
                            <label style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                I am using InkSolver as:
                            </label>
                            {isEnrolledStudent || connectedTutors.length > 0 ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        background: "#eff6ff",
                                        border: "1px solid #bfdbfe",
                                        borderRadius: "10px",
                                        padding: "12px 18px"
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                        <span style={{ fontSize: "22px" }}>🎓</span>
                                        <div>
                                            <div style={{ fontWeight: "700", color: "#1e40af", fontSize: "14px" }}>
                                                Student Account
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#3b82f6" }}>
                                                Enrolled in active classroom(s). Tutor role is locked for enrolled students.
                                            </div>
                                        </div>
                                    </div>
                                    <span
                                        style={{
                                            fontSize: "11px",
                                            fontWeight: "700",
                                            background: "#dbeafe",
                                            color: "#1d4ed8",
                                            padding: "4px 10px",
                                            borderRadius: "999px"
                                        }}
                                    >
                                        Verified Student
                                    </span>
                                </div>
                            ) : (
                                <div style={{ display: "flex", gap: "12px" }}>
                                    <button
                                        type="button"
                                        className={role === "student" ? "primary-button" : "secondary-button"}
                                        onClick={() => setRole("student")}
                                        style={{ flex: 1, padding: "10px 16px", fontSize: "14px", fontWeight: "600" }}
                                    >
                                        <span>🎓 Student</span>
                                    </button>
                                    <button
                                        type="button"
                                        className={role === "tutor" ? "primary-button" : "secondary-button"}
                                        onClick={() => setRole("tutor")}
                                        style={{ flex: 1, padding: "10px 16px", fontSize: "14px", fontWeight: "600" }}
                                    >
                                        <span>🧑‍🏫 Tutor / Teacher</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {role === "tutor" && inviteCode && (
                            <div
                                style={{
                                    backgroundColor: "#f5f3ff",
                                    border: "1px solid #ddd6fe",
                                    borderRadius: "10px",
                                    padding: "14px 18px",
                                    marginBottom: "20px"
                                }}
                            >
                                <div style={{ fontSize: "11px", color: "#6d28d9", fontWeight: "600", textTransform: "uppercase", marginBottom: "4px" }}>
                                    Your Classroom Invite Code
                                </div>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <span style={{ fontFamily: "monospace", fontSize: "20px", fontWeight: "700", color: "#4f46e5" }}>
                                        {inviteCode}
                                    </span>
                                    <button
                                        type="button"
                                        className="secondary-button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(inviteCode);
                                            setCopiedInviteCode(true);
                                            setTimeout(() => setCopiedInviteCode(false), 2000);
                                        }}
                                        style={{ fontSize: "12px", padding: "5px 12px" }}
                                    >
                                        {copiedInviteCode ? "Copied! ✓" : "Copy Code"}
                                    </button>
                                </div>
                                <div style={{ fontSize: "12px", color: "var(--muted)", marginTop: "4px" }}>
                                    Share this code with your students to have them connect to your classroom roster.
                                </div>
                            </div>
                        )}

                        <p className="profile-section-title">Personal details</p>
                        <div className="profile-field-row">
                            <div className="form-group">
                                <label>Full name</label>
                                <input
                                    type="text"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    placeholder="Aanya Sharma"
                                />
                            </div>

                            <div className="form-group">
                                <label>
                                    Username (must be unique)
                                    <span style={{ color: "red", marginLeft: "3px" }}>*</span>
                                </label>
                                {isUsernameLocked ? (
                                    <>
                                        <input
                                            type="text"
                                            value={username}
                                            readOnly
                                            className="input-readonly"
                                        />
                                        <p className="profile-readonly-note">
                                            🔒 Username can't be changed
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => {
                                                setUsername(e.target.value);
                                                if (usernameError) setUsernameError("");
                                            }}
                                            placeholder="aanya.solves"
                                            style={usernameError ? { borderColor: "red" } : {}}
                                        />
                                        {usernameError && (
                                            <p style={{ color: "red", fontSize: "12px", margin: "4px 0 0 0" }}>
                                                {usernameError}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="profile-field-row">
                            <div className="form-group">
                                <label>Email address</label>
                                <input
                                    type="email"
                                    value={user?.email || ""}
                                    readOnly
                                    className="input-readonly"
                                />
                                <p className="profile-readonly-note">
                                    🔒 Linked to your login, can't be changed
                                </p>
                            </div>

                            <div className="form-group">
                                <label>
                                    Contact number
                                    <span style={{ color: "red", marginLeft: "3px" }}>*</span>
                                </label>
                                <input
                                    type="tel"
                                    maxLength="10"
                                    value={phone}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, "");
                                        if (val.length <= 10) {
                                            setPhone(val);
                                        }
                                        if (phoneError && val.length === 10) {
                                            setPhoneError("");
                                        }
                                    }}
                                    onBlur={() => {
                                        if (phone && phone.length !== 10) {
                                            setPhoneError("Phone number must be 10 digits");
                                        } else {
                                            setPhoneError("");
                                        }
                                    }}
                                    placeholder="9876543210"
                                    style={phoneError ? { borderColor: 'red' } : {}}
                                />
                                {phoneError && (
                                    <p style={{ color: 'red', fontSize: '12px', margin: '4px 0 0 0' }}>
                                        {phoneError}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Tutor specific fields */}
                        {role === "tutor" ? (
                            <div key="role-fields-tutor">
                                <p className="profile-section-title">Teaching & Coaching details</p>
                                <div className="profile-field-row">
                                    <div className="form-group">
                                        <label>School or Coaching Center</label>
                                        <input
                                            type="text"
                                            value={organization}
                                            onChange={(e) => setOrganization(e.target.value)}
                                            placeholder="e.g. Sharma Math Academy / Delhi Public School"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Subjects Taught</label>
                                        <input
                                            type="text"
                                            value={subjects}
                                            onChange={(e) => setSubjects(e.target.value)}
                                            placeholder="e.g. Mathematics, Calculus, Trigonometry"
                                        />
                                    </div>
                                </div>
                                <div className="form-group" style={{ marginBottom: "18px" }}>
                                    <label>Bio / Description for Students</label>
                                    <textarea
                                        rows="2"
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        placeholder="Brief note about your teaching style and preparation focus..."
                                        style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "13px", boxSizing: "border-box" }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div key="role-fields-student">
                                <p className="profile-section-title">Learning Goal & Exam Track</p>
                                <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 14px 0" }}>
                                    Choose your focus so Erwin AI tunes its hint depth, formula derivations, and speed tactics.
                                </p>
                                <div
                                    className="learning-goal-grid"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                                        gap: "14px",
                                        marginBottom: "22px"
                                    }}
                                >
                                    {/* Track A: Board Exams */}
                                    <div
                                        className={`learning-goal-card ${learningGoal === "boards_and_concepts" ? "active" : ""}`}
                                        onClick={() => setLearningGoal("boards_and_concepts")}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setLearningGoal("boards_and_concepts"); }}
                                        style={{
                                            border: learningGoal === "boards_and_concepts" ? "2px solid #4f46e5" : "2px solid #e2e8f0",
                                            backgroundColor: learningGoal === "boards_and_concepts" ? "#f5f7ff" : "#ffffff",
                                            borderRadius: "12px",
                                            padding: "14px 16px",
                                            cursor: "pointer",
                                            transition: "all 0.18s ease-in-out",
                                            boxShadow: learningGoal === "boards_and_concepts" ? "0 4px 14px rgba(79, 70, 229, 0.12)" : "0 1px 3px rgba(0, 0, 0, 0.04)",
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative"
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ fontSize: "20px", lineHeight: "1" }}>📚</span>
                                                <div style={{ fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                                                    Board Exams & Concepts
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    width: "18px",
                                                    height: "18px",
                                                    borderRadius: "50%",
                                                    border: learningGoal === "boards_and_concepts" ? "none" : "2px solid #cbd5e1",
                                                    background: learningGoal === "boards_and_concepts" ? "#4f46e5" : "transparent",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#ffffff",
                                                    fontSize: "10px",
                                                    fontWeight: "700"
                                                }}
                                            >
                                                {learningGoal === "boards_and_concepts" ? "✓" : ""}
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "8px" }}>
                                            {["CBSE", "ICSE", "Cambridge / IB", "State Boards"].map((badge) => (
                                                <span
                                                    key={badge}
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: "600",
                                                        padding: "2px 7px",
                                                        borderRadius: "4px",
                                                        background: learningGoal === "boards_and_concepts" ? "#e0e7ff" : "#f1f5f9",
                                                        color: learningGoal === "boards_and_concepts" ? "#3730a3" : "#475569"
                                                    }}
                                                >
                                                    {badge}
                                                </span>
                                            ))}
                                        </div>

                                        <p style={{ margin: "0", fontSize: "12px", color: "#64748b", lineHeight: "1.4" }}>
                                            Step-by-step proofs, explicit formulas, and full board scoring criteria.
                                        </p>
                                    </div>

                                    {/* Track B: Competitive Exams */}
                                    <div
                                        className={`learning-goal-card ${learningGoal === "competitive_and_entrance" ? "active" : ""}`}
                                        onClick={() => setLearningGoal("competitive_and_entrance")}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setLearningGoal("competitive_and_entrance"); }}
                                        style={{
                                            border: learningGoal === "competitive_and_entrance" ? "2px solid #4f46e5" : "2px solid #e2e8f0",
                                            backgroundColor: learningGoal === "competitive_and_entrance" ? "#f5f7ff" : "#ffffff",
                                            borderRadius: "12px",
                                            padding: "14px 16px",
                                            cursor: "pointer",
                                            transition: "all 0.18s ease-in-out",
                                            boxShadow: learningGoal === "competitive_and_entrance" ? "0 4px 14px rgba(79, 70, 229, 0.12)" : "0 1px 3px rgba(0, 0, 0, 0.04)",
                                            display: "flex",
                                            flexDirection: "column",
                                            position: "relative"
                                        }}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ fontSize: "20px", lineHeight: "1" }}>⚡</span>
                                                <div style={{ fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                                                    Competitive & Entrance Tests
                                                </div>
                                            </div>
                                            <div
                                                style={{
                                                    width: "18px",
                                                    height: "18px",
                                                    borderRadius: "50%",
                                                    border: learningGoal === "competitive_and_entrance" ? "none" : "2px solid #cbd5e1",
                                                    background: learningGoal === "competitive_and_entrance" ? "#4f46e5" : "transparent",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#ffffff",
                                                    fontSize: "10px",
                                                    fontWeight: "700"
                                                }}
                                            >
                                                {learningGoal === "competitive_and_entrance" ? "✓" : ""}
                                            </div>
                                        </div>

                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: "8px" }}>
                                            {["JEE Main & Adv", "NEET", "SAT Math", "Olympiad"].map((badge) => (
                                                <span
                                                    key={badge}
                                                    style={{
                                                        fontSize: "11px",
                                                        fontWeight: "600",
                                                        padding: "2px 7px",
                                                        borderRadius: "4px",
                                                        background: learningGoal === "competitive_and_entrance" ? "#e0e7ff" : "#f1f5f9",
                                                        color: learningGoal === "competitive_and_entrance" ? "#3730a3" : "#475569"
                                                    }}
                                                >
                                                    {badge}
                                                </span>
                                            ))}
                                        </div>

                                        <p style={{ margin: "0", fontSize: "12px", color: "#64748b", lineHeight: "1.4" }}>
                                            Speed shortcuts, invariants, Pythagorean triplets, and rapid pattern elimination.
                                        </p>
                                    </div>
                                </div>

                                <p className="profile-section-title">School details</p>
                                <div className="profile-field-row">
                                    <div className="form-group">
                                        <label>
                                            Grade
                                            <span style={{ color: "red", marginLeft: "3px" }}>*</span>
                                        </label>
                                        <select
                                            value={grade}
                                            onChange={(e) => setGrade(e.target.value)}
                                        >
                                            <option value="">Select grade</option>
                                            <option value="Grade 9">Grade 9</option>
                                            <option value="Grade 10">Grade 10</option>
                                            <option value="Grade 11">Grade 11</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>School name</label>
                                        <input
                                            type="text"
                                            value={schoolName}
                                            onChange={(e) => setSchoolName(e.target.value)}
                                            placeholder="Delhi Public School"
                                        />
                                    </div>
                                </div>

                                <p className="profile-section-title">My Tutors & Classrooms</p>
                                {connectedTutors.length > 0 && (
                                    <div style={{ marginBottom: "16px" }}>
                                        <p style={{ fontSize: "13px", color: "var(--muted)", margin: "0 0 10px 0" }}>
                                            You are enrolled in the following classrooms. Homework and assignments from these teachers appear in your Assigned Homework tab:
                                        </p>
                                        {connectedTutors.map((t) => (
                                            <div
                                                key={t.uid}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    backgroundColor: "#f8fafc",
                                                    border: "1px solid #e2e8f0",
                                                    borderRadius: "10px",
                                                    padding: "12px 16px",
                                                    marginBottom: "10px",
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                                                }}
                                            >
                                                <div>
                                                    <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "14px", display: "flex", alignItems: "center", gap: "8px" }}>
                                                        <span>🧑‍🏫 {t.name}</span>
                                                        {t.username && <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "500" }}>@{t.username}</span>}
                                                    </div>
                                                    <div style={{ fontSize: "12px", color: "#475569", marginTop: "3px", display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                                                        {t.organization && <span style={{ fontWeight: "600", color: "#1e40af" }}>{t.organization}</span>}
                                                        {t.organization && <span>•</span>}
                                                        <span style={{ background: "#e0e7ff", color: "#3730a3", padding: "1px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "600" }}>
                                                            {t.subjects?.length ? t.subjects.join(", ") : "Mathematics"}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="secondary-button"
                                                    onClick={() => handleDisconnectTutor(t.uid)}
                                                    style={{ fontSize: "12px", padding: "4px 10px", color: "#dc2626", borderColor: "#fca5a5", background: "#ffffff" }}
                                                >
                                                    Leave Classroom
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div style={{ marginBottom: "18px", background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: "10px", padding: "14px 16px" }}>
                                    <div style={{ fontWeight: "600", fontSize: "13px", color: "#334155", marginBottom: "4px" }}>
                                        {connectedTutors.length > 0 ? "+ Connect another Tutor (e.g. for Physics, Chemistry, Calculus)" : "Connect with your Tutor or Teacher"}
                                    </div>
                                    <p style={{ fontSize: "12px", color: "var(--muted)", margin: "0 0 10px 0" }}>
                                        {connectedTutors.length > 0
                                            ? "Have different teachers for different subjects? Enter their unique code below to add them to your classroom list."
                                            : "Enter your teacher's 6-character classroom code (e.g. TUT-XXXXXX) or username to receive homework assignments."}
                                    </p>
                                    {autoFilledCode && (
                                        <div style={{ fontSize: "11.5px", color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", padding: "4px 10px", borderRadius: "6px", marginBottom: "10px", display: "inline-block" }}>
                                            ✨ Classroom code auto-filled from your invitation: <strong>{autoFilledCode}</strong>
                                        </div>
                                    )}
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <input
                                            type="text"
                                            value={tutorJoinCode}
                                            onChange={(e) => setTutorJoinCode(e.target.value)}
                                            placeholder="Enter Tutor Code (e.g. TUT-XXXXXX) or username"
                                            style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", border: "1px solid var(--border)", fontSize: "13px", background: "#ffffff" }}
                                        />
                                        <button
                                            type="button"
                                            className="primary-button"
                                            onClick={handleJoinTutor}
                                            style={{ fontSize: "13px", padding: "8px 18px", whiteSpace: "nowrap" }}
                                        >
                                            Connect
                                        </button>
                                    </div>
                                    {tutorJoinError && <div style={{ color: "#dc2626", fontSize: "12px", marginTop: "6px" }}>{tutorJoinError}</div>}
                                    {tutorJoinStatus && <div style={{ color: "#059669", fontSize: "12px", marginTop: "6px" }}>{tutorJoinStatus}</div>}
                                </div>
                            </div>
                        )}

                        <div className="profile-field-row">
                            <div className="form-group">
                                <label>City</label>
                                <input
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="Hyderabad"
                                />
                            </div>

                            <div className="form-group">
                                <label>State</label>
                                <select
                                    value={stateName}
                                    onChange={(e) => setStateName(e.target.value)}
                                >
                                    <option value="">Select state</option>
                                    <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                                    <option value="Andhra Pradesh">Andhra Pradesh</option>
                                    <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                                    <option value="Assam">Assam</option>
                                    <option value="Bihar">Bihar</option>
                                    <option value="Chandigarh">Chandigarh</option>
                                    <option value="Chhattisgarh">Chhattisgarh</option>
                                    <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                                    <option value="Delhi">Delhi</option>
                                    <option value="Goa">Goa</option>
                                    <option value="Gujarat">Gujarat</option>
                                    <option value="Haryana">Haryana</option>
                                    <option value="Himachal Pradesh">Himachal Pradesh</option>
                                    <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                                    <option value="Jharkhand">Jharkhand</option>
                                    <option value="Karnataka">Karnataka</option>
                                    <option value="Kerala">Kerala</option>
                                    <option value="Ladakh">Ladakh</option>
                                    <option value="Lakshadweep">Lakshadweep</option>
                                    <option value="Madhya Pradesh">Madhya Pradesh</option>
                                    <option value="Maharashtra">Maharashtra</option>
                                    <option value="Manipur">Manipur</option>
                                    <option value="Meghalaya">Meghalaya</option>
                                    <option value="Mizoram">Mizoram</option>
                                    <option value="Nagaland">Nagaland</option>
                                    <option value="Odisha">Odisha</option>
                                    <option value="Puducherry">Puducherry</option>
                                    <option value="Punjab">Punjab</option>
                                    <option value="Rajasthan">Rajasthan</option>
                                    <option value="Sikkim">Sikkim</option>
                                    <option value="Tamil Nadu">Tamil Nadu</option>
                                    <option value="Telangana">Telangana</option>
                                    <option value="Tripura">Tripura</option>
                                    <option value="Uttar Pradesh">Uttar Pradesh</option>
                                    <option value="Uttarakhand">Uttarakhand</option>
                                    <option value="West Bengal">West Bengal</option>
                                </select>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="profile-actions">
                            <button className="secondary-button" onClick={handleCancel}>
                                Cancel
                            </button>
                            <button
                                className="primary-button"
                                onClick={handleSave}
                                disabled={saveStatus === "saving"}
                            >
                                {saveStatus === "saving"
                                    ? "Saving..."
                                    : saveStatus === "saved"
                                        ? "Saved ✓"
                                        : "Save changes"}
                            </button>
                        </div>
                        {saveError && (
                            <div className="error-text">{saveError}</div>
                        )}
                    </div>
                    {/* end right card */}
                </div>
                </div>
                )}
                {activePanel === "calibration" && (
                <section id="profile-calibration-panel" className="profile-writing-device-card profile-expanded-panel" aria-labelledby="writing-device-title">
                    <div className="profile-writing-device-heading">
                        <div>
                            <p className="profile-section-title">Writing device</p>
                            <h3 id="writing-device-title">Calibrate your writing with a stylus</h3>
                            <p>InkSolver uses a setup saved for this browser and device in Practice and Ink Together. You can skip it, calibrate now, or return whenever your pen or writing feel changes.</p>
                        </div>
                        <span className={`profile-calibration-status ${inkCalibrated ? "is-ready" : ""}`}>
                            {inkCalibrated ? "Calibrated" : "Optional"}
                        </span>
                    </div>

                    {inkCalibrated ? (
                        <>
                            <div className="profile-writing-device-summary">
                                <div><span>Writing tool</span><strong>{getInkWritingToolLabel(inkSettings.writingTool)}</strong></div>
                                <div><span>Ink feel</span><strong>{getInkFeelLabel(inkSettings)}</strong></div>
                                <div><span>Pen thickness</span><strong>{inkSettings.lineWidth.toFixed(2)} px</strong></div>
                                <div><span>Aim</span><strong>{inkSettings.nibOffsetX || inkSettings.nibOffsetY ? "Adjusted" : "Centered"}</strong></div>
                                <div><span>Palm protection</span><strong>{inkSettings.palmCalibrationReady ? `${inkSettings.palmStylusSeparationPx.toFixed(0)} px` : "Not calibrated"}</strong></div>
                                <div><span>Last saved</span><strong>{formatCalibrationDate(inkDeviceProfile?.calibratedAt)}</strong></div>
                            </div>
                            <details className="profile-writing-device-details">
                                <summary>View saved rendering settings</summary>
                                <dl>
                                    <div><dt>Horizontal nib offset</dt><dd>{inkSettings.nibOffsetX.toFixed(1)} px</dd></div>
                                    <div><dt>Vertical nib offset</dt><dd>{inkSettings.nibOffsetY.toFixed(1)} px</dd></div>
                                    <div><dt>Point spacing</dt><dd>{inkSettings.minPointDistance.toFixed(2)} px</dd></div>
                                    <div><dt>Captured points</dt><dd>{inkSettings.coalescedLimit}</dd></div>
                                    <div><dt>Smoothing</dt><dd>{Math.round(inkSettings.smoothingStrength * 100)}%</dd></div>
                                    <div><dt>Fast response</dt><dd>{Math.round(inkSettings.velocityResponse * 100)}%</dd></div>
                                    <div><dt>Latency lead</dt><dd>{inkSettings.predictionMs.toFixed(0)} ms</dd></div>
                                    <div><dt>Gap repair</dt><dd>{inkSettings.interpolationSegmentLength ? `${inkSettings.interpolationSegmentLength.toFixed(1)} px` : "Off"}</dd></div>
                                </dl>
                            </details>
                        </>
                    ) : (
                        <div className="profile-writing-device-empty">
                            <strong>Standard slate settings are active.</strong>
                            <span>Calibration is optional and can be completed at any time.</span>
                        </div>
                    )}

                    <div className="profile-writing-device-actions">
                        <button type="button" className="primary-button" onClick={() => navigate("/device-check")}>
                            {inkCalibrated ? "Edit calibration" : "Calibrate writing"}
                        </button>
                    </div>
                </section>
                )}
            </div>
        </section>
    );
}

export default Profile;
